import { FunctionComponent, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { StaffLevelRights, StaffWithLinks } from '@/types/Staff'
import { NavigateFunction, useNavigate } from 'react-router-dom'
import { useAuth } from '@/modules/context/AuthContext'
import styles from './staffProfile.module.scss'
import Title from '@/components/_custom/Title/title'
import { Role } from '@/types/Role'
import UserInput from '@/components/_custom/UserInput/userInput'
import { convertDegreeToString } from '@/modules/helper/staffHelper'
import { KeyboardArrowDown, KeyboardArrowUp, LinkSharp, Merge, Search } from '@mui/icons-material'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import { GET_STAFF } from '@/modules/API/staff/staffHandler'
import { useLazyQuery, useMutation } from '@apollo/client'
import Loading from '@/components/Loading/Loading'
import ErrorMessage from '@/components/_custom/ErrorMessage/ErrorMessage'
import ConfirmAssociateStaffDialog from '../../../suggest/suggestTable/suggestStaffTable/Dialog/confirmAssociateStaffDialog'
import CountrySelect from '@/components/_custom/CountrySelect/countrySelect'
import TaskAndNotesStaff from './taskAndNotes/taskAndNotesStaff.tsx'
import { Daycare } from '@/types/Daycare.ts'
import SelectUnique from '@/components/_custom/Selector/selectUnique/selectUnique.tsx'
import { SelectOptionDeprecated, SelectOptionsDeprecated } from '@/types/Select.ts'
import PermissionsTable from '../StaffList/Dialog/permissionsTable.tsx'
import { useStaffPermissions } from '@/hooks/useStaffPermissions.ts'
import { StaffLevel } from '@/types/StaffLevel.ts'
import { StaffRights } from '@/types/StaffRights.ts'
import { mapPermissionsToGraphQLVars } from '@/modules/helper/permissionMapper.ts'
import { UPDATE_STAFF_RIGHTS } from '@/modules/API/daycare/staffLinkHandler.ts'
import { useNotification } from '@/modules/context/NotificationContext.tsx'
import { UPDATE_STAFF_LINK_LEVEL } from '@/modules/API/staff/staffLevels.ts'
import SearchStaffsDialog from './Dialog/searchStaffsDialog.tsx'
import MergeTwoStaffsDialog from './Dialog/mergeTwoStaffsDialog.tsx'

interface StaffProfileProps {
    staffId: number
    refetchAllStaffList: () => Promise<void>
    staffsListData: Array<StaffWithLinks>
}

const StaffProfile: FunctionComponent<StaffProfileProps> = ({
    staffId,
    refetchAllStaffList,
    staffsListData
}: StaffProfileProps) => {
    const { t } = useTranslation()

    const { user } = useAuth()

    const navigate: NavigateFunction = useNavigate()

    const { showNotificationSuccess, showNotificationError } = useNotification()

    const [staff, setStaff] = useState<StaffWithLinks | null>(null)

    const [showAddress, setShowAddress] = useState<boolean>(false)
    const [isConfirmAssociateDialogOpen, setConfirmAssociateDialogOpen] = useState<boolean>(false)
    const [showSearchStaffDialogOpen, setSearchStaffDialogOpen] = useState<boolean>(false)
    const [showMergeStaffDialogOpen, setMergeStaffDialogOpen] = useState<boolean>(false)

    const [showPersonalInformation, setShowPersonalInformation] = useState<boolean>(true)
    const [showDaycareInformation, setShowDaycareInformation] = useState<boolean>(true)

    const [staffDaycare, setStaffDaycare] = useState<Daycare | undefined>()
    const [selectOptionsDaycare, setSelectOptionsDaycare] = useState<SelectOptionsDeprecated>([])

    const [selectedDaycareHaveTaskNote, setSelectedDaycareHaveTaskNote] = useState<boolean>(false)

    const [daycareStaffLinks, setDaycareStaffLinks] = useState<StaffLevelRights | undefined>(undefined)

    const [getStaff, { loading: loadingStaff, data: dataStaff, refetch: refetchStaff }] = useLazyQuery(GET_STAFF, {
        variables: {
            staffId: staffId
        },
        fetchPolicy: 'network-only'
    })

    useEffect(() => {
        if (dataStaff && user) {
            setStaff(dataStaff.getStaff)
            const daycares: Daycare[] = []
            for (const staffLink of dataStaff.getStaff.staffLinks) {
                const daycareFound: Daycare | undefined = user.daycares.find(
                    (daycare: Daycare): boolean => daycare.id === staffLink.daycareId
                )
                if (daycareFound) {
                    daycares.push(daycareFound)
                    if (user.currentDaycare?.id === daycareFound.id) {
                        setStaffDaycare(daycareFound)
                        setSelectedDaycareHaveTaskNote(daycareFound.taskNoteAccess === true)
                        setDaycareStaffLinks(staffLink)
                    } else if (staffDaycare === undefined) {
                        setStaffDaycare(daycareFound)
                        setSelectedDaycareHaveTaskNote(daycareFound.taskNoteAccess === true)
                        setDaycareStaffLinks(staffLink)
                    }
                }
            }
            setSelectOptionsDaycare([
                {
                    options: daycares.map((daycare: Daycare): SelectOptionDeprecated => {
                        return {
                            value: daycare.id.toString(),
                            name: daycare.name
                        }
                    })
                }
            ])
        }
    }, [dataStaff])

    async function refetchOneStaff(): Promise<void> {
        const { data } = await refetchStaff()
        if (data && user) {
            setStaff(data.getStaff)
            const daycares: Daycare[] = []
            for (const staffLink of data.getStaff.staffLinks) {
                const daycareFound: Daycare | undefined = user.daycares.find(
                    (daycare: Daycare): boolean => daycare.id === staffLink.daycareId
                )
                if (daycareFound) {
                    daycares.push(daycareFound)
                    if (user.currentDaycare?.id === daycareFound.id) {
                        setStaffDaycare(daycareFound)
                        setSelectedDaycareHaveTaskNote(daycareFound.taskNoteAccess === true)
                        setDaycareStaffLinks(staffLink)
                    } else if (staffDaycare === undefined) {
                        setStaffDaycare(daycareFound)
                        setSelectedDaycareHaveTaskNote(daycareFound.taskNoteAccess === true)
                        setDaycareStaffLinks(staffLink)
                    }
                }
            }
            setSelectOptionsDaycare([
                {
                    options: daycares.map((daycare: Daycare): SelectOptionDeprecated => {
                        return {
                            value: daycare.id.toString(),
                            name: daycare.name
                        }
                    })
                }
            ])
        }
    }

    function handleGoBack(): void {
        navigate(-1)
    }

    function handleSetSelectedDaycare(id: string): void {
        const daycare: Daycare | undefined = user?.daycares.find(
            (daycare: Daycare): boolean => daycare.id.toString() === id
        )
        if (daycare) {
            setSelectedDaycareHaveTaskNote(daycare.taskNoteAccess === true)
            setStaffDaycare(daycare)
            setDaycareStaffLinks(
                staff
                    ? staff.staffLinks.find(
                          (staffLink: StaffLevelRights): boolean => staffLink.daycareId === daycare.id
                      )
                    : undefined
            )
        }
    }

    const {
        permissions,
        staffLevel,
        setPermission,
        setStaffLevel,
        toggleAllRead,
        toggleAllWrite,
        allReadChecked,
        allWriteChecked,
        readIndeterminate,
        writeIndeterminate,
        isDirty
    } = useStaffPermissions({
        initialRights: daycareStaffLinks || null,
        initialLevel: daycareStaffLinks?.level?.toString() ?? 'no',
        addNewsWithoutValidationDisabled:
            staffDaycare?.haveDaycareGroup === true && !daycareStaffLinks?.w_add_posts && user?.role === Role.staff
    })

    useEffect(() => {
        getStaff()
    }, [staffId])

    const self: boolean = user && staff ? staff.email === user.email : false

    const levelsOptions: SelectOptionsDeprecated = useMemo(
        (): SelectOptionsDeprecated => [
            {
                options: [
                    { value: 'no', name: t('noStaffLevels') },
                    ...(staffDaycare && staffDaycare.levels
                        ? staffDaycare.levels.map(
                              (level: StaffLevel): SelectOptionDeprecated => ({
                                  value: level.id.toString(),
                                  name: level.name
                              })
                          )
                        : [])
                ]
            }
        ],
        [staffDaycare, t]
    )

    const [updateStaffLevel] = useMutation(UPDATE_STAFF_LINK_LEVEL, {
        onCompleted: () => {
            showNotificationSuccess(t('staffLevelUpdated'))
            refetchOneStaff()
        },
        onError: () => {
            showNotificationError(t('errorUpdatedStaffLevel'))
        }
    })

    const [updateStaffRights] = useMutation(UPDATE_STAFF_RIGHTS, {
        onCompleted: () => {
            showNotificationSuccess(t('staffAccessUpdated'))
            refetchOneStaff()
        },
        onError: () => {
            showNotificationError(t('errorUpdatedStaffAccess'))
        }
    })

    const handleUpdateLevel: (value: string) => void = (value: string): void => {
        setStaffLevel(value)
        if (value !== (daycareStaffLinks?.level?.toString() ?? 'no')) {
            updateStaffLevel({
                variables: {
                    staffLinkId: staff?.id,
                    daycareId: staffDaycare?.id,
                    staffLevelId: parseInt(value)
                }
            })
        }
    }

    const handleUpdateRights: () => void = (): void => {
        if (staff && !self) {
            const graphQLVars: StaffRights = mapPermissionsToGraphQLVars(permissions)
            updateStaffRights({
                variables: {
                    staff_id: staff.id,
                    daycare_id: staffDaycare?.id,
                    ...graphQLVars
                }
            })
        }
    }

    const canWrite: boolean = !!user?.currentDaycareRights.canWriteStaffRights

    return (
        <>
            {!loadingStaff && user && staff ? (
                <div className={styles.PagePosition}>
                    {user?.role === Role.cald && staff.staffLinks.length <= 1 && (
                        <>
                            {staff.staffLinks.length === 0 ? (
                                <div className={styles.NoDaycareCALD}>
                                    <ErrorMessage error={t('noDaycare')} className={styles.Error} />
                                </div>
                            ) : (
                                staffDaycare &&
                                user.currentDaycare &&
                                staffDaycare.id !== user.currentDaycare.id && (
                                    <div className={styles.NoDaycareCALD}>
                                        <ErrorMessage
                                            error={staffDaycare.city + ' - ' + staffDaycare.name}
                                            className={styles.Info}
                                        />
                                    </div>
                                )
                            )}
                        </>
                    )}
                    <div className={styles.ProfilPlacement}>
                        <div className={styles.Form}>
                            <div
                                className={styles.Title}
                                onClick={() => setShowPersonalInformation(!showPersonalInformation)}
                            >
                                <Title text={self ? t('myPersonalInformation') : t('personalInformation')} />
                                {showPersonalInformation ? (
                                    <KeyboardArrowUp className={styles.Icon} />
                                ) : (
                                    <KeyboardArrowDown className={styles.Icon} />
                                )}
                            </div>
                            {showPersonalInformation && (
                                <>
                                    <div className={styles.InputContainer}>
                                        {user.role === Role.cald && (
                                            <div className={styles.Input}>
                                                <UserInput
                                                    label={t('staffId')}
                                                    value={staff.id.toString()}
                                                    readOnly
                                                    className={styles.UserInputAlone}
                                                />
                                            </div>
                                        )}
                                        <div className={styles.Input}>
                                            <UserInput
                                                label={t('firstname')}
                                                value={staff.firstname}
                                                readOnly
                                                className={styles.UserInput}
                                            />
                                            <UserInput
                                                label={t('lastname')}
                                                value={staff.lastname}
                                                readOnly
                                                className={styles.UserInput}
                                            />
                                        </div>
                                        {staff.email && staff.phone && (
                                            <div className={styles.Input}>
                                                <UserInput
                                                    label={t('email')}
                                                    value={staff.email}
                                                    readOnly
                                                    type={'email'}
                                                    className={styles.UserInput}
                                                />
                                                <UserInput
                                                    label={t('phone')}
                                                    value={staff.phone}
                                                    readOnly
                                                    type={'phone'}
                                                    className={styles.UserInput}
                                                />
                                            </div>
                                        )}
                                        <div className={styles.Input}>
                                            <UserInput
                                                label={t('diploma')}
                                                value={convertDegreeToString(staff.degree, t)}
                                                readOnly
                                                className={styles.UserInput}
                                            />
                                        </div>
                                    </div>
                                    {staff.city && staff.country && (
                                        <>
                                            <div className={styles.Title} onClick={() => setShowAddress(!showAddress)}>
                                                <Title text={t('address')} />
                                                {showAddress ? (
                                                    <KeyboardArrowUp className={styles.Icon} />
                                                ) : (
                                                    <KeyboardArrowDown className={styles.Icon} />
                                                )}
                                            </div>
                                            {showAddress && (
                                                <>
                                                    <div className={styles.InputContainer}>
                                                        <div className={styles.Input}>
                                                            <UserInput
                                                                label={t('address')}
                                                                value={staff.address}
                                                                readOnly
                                                                className={styles.UserInput}
                                                            />
                                                            <UserInput
                                                                label={t('city')}
                                                                value={staff.city}
                                                                readOnly
                                                                className={styles.UserInput}
                                                            />
                                                        </div>
                                                        <div className={styles.Input}>
                                                            <UserInput
                                                                label={t('zipCode')}
                                                                value={staff.zip}
                                                                readOnly
                                                                className={styles.UserInput}
                                                            />
                                                            <div className={styles.UserInput}>
                                                                <CountrySelect country={staff.country} readOnly />
                                                            </div>
                                                        </div>
                                                    </div>
                                                </>
                                            )}
                                        </>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                    {daycareStaffLinks && canWrite && (
                        <div className={styles.StaffPlacement}>
                            <div className={styles.TitleAndButton}>
                                <div
                                    className={styles.Title}
                                    onClick={() => setShowDaycareInformation(!showDaycareInformation)}
                                >
                                    <Title text={t('daycareInformation')} />
                                    {showDaycareInformation ? (
                                        <KeyboardArrowUp className={styles.Arrow} />
                                    ) : (
                                        <KeyboardArrowDown className={styles.Arrow} />
                                    )}
                                </div>
                                {staffDaycare && selectOptionsDaycare[0].options.length > 1 && (
                                    <div className={styles.Input}>
                                        <SelectUnique
                                            selectOptions={selectOptionsDaycare}
                                            selectValue={staffDaycare?.id.toString()}
                                            setSelectValue={handleSetSelectedDaycare}
                                            label={t('structure')}
                                            className={styles.UserInput}
                                        />
                                    </div>
                                )}
                            </div>
                            {showDaycareInformation && (
                                <div className={styles.DaycarePlacement}>
                                    <div className={styles.SelectContainer}>
                                        <SelectUnique
                                            selectOptions={levelsOptions}
                                            selectValue={staffLevel}
                                            setSelectValue={handleUpdateLevel}
                                            formClassName={styles.SelectInput}
                                            readOnly={!canWrite}
                                            label={t('level')}
                                        />
                                    </div>
                                    <div className={styles.PermissionsTablePlacement}>
                                        <PermissionsTable
                                            permissions={permissions}
                                            setPermission={setPermission}
                                            allReadChecked={allReadChecked}
                                            allWriteChecked={allWriteChecked}
                                            readIndeterminate={readIndeterminate}
                                            writeIndeterminate={writeIndeterminate}
                                            toggleAllRead={toggleAllRead}
                                            toggleAllWrite={toggleAllWrite}
                                            disabled={self || !canWrite}
                                            currentDaycare={staffDaycare}
                                            userRole={user?.role}
                                            currentDaycareRightsAddNewsWithoutValidation={
                                                user?.currentDaycareRights.canAddNewsWithoutValidation
                                            }
                                            showTitleInTable={true}
                                        />
                                        {canWrite && (
                                            <div className={styles.ButtonContainer}>
                                                <ButtonTextIcon
                                                    type='update'
                                                    onClick={handleUpdateRights}
                                                    className={styles.Button}
                                                    disabled={!isDirty}
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                            {staffDaycare &&
                                user?.currentDaycareRights.canReadTaskNote &&
                                selectedDaycareHaveTaskNote && (
                                    <TaskAndNotesStaff
                                        daycare={staffDaycare}
                                        staffId={staff.id}
                                        staffName={staff.firstname + ' ' + staff.lastname}
                                        canUpdate={user.currentDaycareRights.canWriteTaskNote}
                                        staffsListData={staffsListData}
                                    />
                                )}
                        </div>
                    )}
                    <div className={styles.ButtonsPlacement}>
                        <ButtonTextIcon
                            type={'return'}
                            className={staffDaycare ? styles.Button : styles.ButtonReduce}
                            onClick={handleGoBack}
                        />
                        {staff.staffLinks.length === 0 && user.currentDaycareRights.canWriteSuggestions ? (
                            <>
                                <ButtonTextIcon
                                    type={'custom'}
                                    customText={t('associate')}
                                    customIcon={<LinkSharp />}
                                    onClick={() => setConfirmAssociateDialogOpen(true)}
                                    className={styles.Button}
                                    disabled={!user?.currentDaycareRights.canWriteStaffRights}
                                />
                                {isConfirmAssociateDialogOpen && staff.email !== '' && (
                                    <ConfirmAssociateStaffDialog
                                        open={isConfirmAssociateDialogOpen}
                                        closeDialog={() => setConfirmAssociateDialogOpen(false)}
                                        name={staff.lastname + ' ' + staff.firstname}
                                        email={staff.email}
                                        refetchAllSuggest={async () => {
                                            await refetchAllStaffList()
                                            await refetchOneStaff()
                                        }}
                                    />
                                )}
                            </>
                        ) : (
                            staffDaycare && (
                                <>
                                    <ButtonTextIcon
                                        type={'custom'}
                                        customIcon={<Search />}
                                        customText={t('searchStaff')}
                                        onClick={() => setSearchStaffDialogOpen(true)}
                                        className={styles.Button}
                                    />
                                    {user?.currentDaycareRights.canWriteFamilies && staff.staffLinks.length >= 1 && (
                                        <ButtonTextIcon
                                            type={'custom'}
                                            customText={t('merge')}
                                            customIcon={<Merge />}
                                            className={styles.Button}
                                            onClick={() => setMergeStaffDialogOpen(true)}
                                        />
                                    )}
                                    {staffDaycare && showSearchStaffDialogOpen && (
                                        <SearchStaffsDialog
                                            open={showSearchStaffDialogOpen}
                                            handleClose={() => setSearchStaffDialogOpen(false)}
                                            staffId={staff.id}
                                            staffsListData={staffsListData}
                                        />
                                    )}
                                    {staffDaycare && showMergeStaffDialogOpen && (
                                        <MergeTwoStaffsDialog
                                            open={showMergeStaffDialogOpen}
                                            handleClose={() => setMergeStaffDialogOpen(false)}
                                            staffId={staff.id}
                                            staffsListData={staffsListData}
                                            handleRefetchStaff={async () => {
                                                await refetchAllStaffList()
                                                await refetchOneStaff()
                                            }}
                                            staffName={
                                                staff.firstname && staff.lastname
                                                    ? staff.firstname + ' ' + staff.lastname
                                                    : staff.email
                                            }
                                            daycareId={staffDaycare.id}
                                        />
                                    )}
                                </>
                            )
                        )}
                    </div>
                </div>
            ) : (
                <Loading overall />
            )}
        </>
    )
}

export default StaffProfile
